import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Chip, CircularProgress, Stack, Tooltip,
} from '@mui/material';
import grapeRulesService, { BlendDisplay } from '../../services/grapeRulesService';

/**
 * GK-3 §6 — the operator's view of what a record's grapes are.
 *
 * Every layer it has, each labelled with where it came from. Density is
 * correct here: this is the adjudication surface, and an operator deciding
 * between a label and a producer's sheet needs to see both, not the
 * winner.
 *
 * Two things it deliberately does not do:
 *   - it never shows PERMITTED as a composition. A constraint is not a
 *     description; that belongs on the region's screen and in a finding's
 *     reason, never in the slot where a blend goes.
 *   - it never aggregates a cuvée blend from its vintages. On a wine name
 *     the absence of an actual blend is stated, with a pointer to where
 *     the actuals live, so absence cannot be misread as "no data".
 */

const LAYER_COLOUR: Record<string, 'success' | 'warning' | 'default'> = {
  actual: 'success',
  characteristic: 'warning',
  declared: 'default',
};

const LAYER_HELP: Record<string, string> = {
  actual: "This vintage's blend, from the producer's own sheet for this year.",
  characteristic: 'What the producer says the wine is usually made of. An approximation of this bottle, not a statement about it.',
  declared: 'What the label prints.',
};

interface Props {
  wineId?: string;
  wineNameId?: string;
}

const BlendLayersPanel: React.FC<Props> = ({ wineId, wineNameId }) => {
  const [data, setData] = useState<BlendDisplay | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    if (!wineId && !wineNameId) return;
    setLoading(true);
    try {
      setData(await grapeRulesService.blendDisplay({ wineId, wineNameId }));
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [wineId, wineNameId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Paper>
    );
  }
  // A failed lookup says nothing rather than implying the record has no grapes.
  if (failed || !data) return null;

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>Grapes</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each source shown separately, most precise first. These are different claims, not one
        answer — where they disagree, that disagreement is the finding.
      </Typography>

      {data.layers.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nothing recorded — no label grapes, and no producer sheet has been read for this wine.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {data.layers.map((l) => (
            <Box key={l.layer} sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <Tooltip title={LAYER_HELP[l.layer] ?? ''}>
                <Chip size="small" label={l.badge} color={LAYER_COLOUR[l.layer] ?? 'default'} variant="outlined" />
              </Tooltip>
              <Typography variant="body1">{l.text}</Typography>
              {l.precision !== 'declared' && (
                <Typography variant="caption" color="text.secondary">({l.precision})</Typography>
              )}
            </Box>
          ))}
        </Stack>
      )}

      {/* §3.2 — say it, so absence is not read as "no data". */}
      {data.actualBlendsLiveOnVintages && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
          Each vintage&apos;s own blend lives on that vintage. Nothing here is averaged from them —
          a cuvée blend is only ever what the producer stated.
        </Typography>
      )}
    </Paper>
  );
};

export default BlendLayersPanel;
